<template>
    <div>
        <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center mb-2" :style="{ gap: '8px' }">
                <el-radio-group v-model="filters.always_active" size="small">
                    <el-radio-button :label="null">Todos</el-radio-button>
                    <el-radio-button :label="true">Sempre ativos</el-radio-button>
                    <el-radio-button :label="false">Temporários</el-radio-button>
                </el-radio-group>
            </div>
        </div>

        <el-table
            class="rounded"
            v-loading="loading"
            :data="data"
            align="center"
            border
        >
            <!-- ID -->
            <el-table-column prop="id" label="ID" width="70" align="center" />

            <!-- Imagem -->
            <el-table-column label="Imagem" width="100" align="center">
                <template slot-scope="scope">
                    <el-image
                        :style="{ width: '60px', height: '40px' }"
                        :src="scope.row.url"
                        :preview-src-list="[`${scope.row.url}`]"
                        fit="cover"
                    >
                        <template slot="error">
                            <div class="d-flex align-items-center justify-content-center border rounded" :style="{ height: '100%' }">
                                <i class="el-icon-picture-outline"></i>
                            </div>
                        </template>
                    </el-image>
                </template>
            </el-table-column>

            <!-- Titulo -->
            <el-table-column label="Título" min-width="200" align="left">
                <template slot-scope="scope">
                    <span class="font-weight-bold">{{ scope.row.title }}</span>
                </template>
            </el-table-column>

            <!-- Subtitulo -->
            <el-table-column label="Subtítulo" min-width="200" align="left">
                <template slot-scope="scope">
                    <span>{{ scope.row.subTitle || '—' }}</span>
                </template>
            </el-table-column>

            <!-- Botao -->
            <el-table-column label="Botão" width="90" align="center">
                <template slot-scope="scope">
                    <el-tag size="small" :type="scope.row.have_button ? 'success' : 'info'">
                        {{ scope.row.have_button ? 'SIM' : 'NÃO' }}
                    </el-tag>
                </template>
            </el-table-column>

            <!-- Texto do botao -->
            <el-table-column label="Texto do botão" width="150" align="left">
                <template slot-scope="scope">
                    <span>{{ scope.row.have_button ? (scope.row.button_text || '—') : '—' }}</span>
                </template>
            </el-table-column>

            <!-- Link do botao -->
            <el-table-column label="Link do botão" min-width="200" align="left">
                <template slot-scope="scope">
                    <el-link
                        v-if="scope.row.have_button && scope.row.action_button_link"
                        :href="scope.row.action_button_link"
                        target="_blank"
                        type="primary"
                        :underline="false"
                        class="d-inline-block text-truncate"
                        :style="{ maxWidth: '180px' }"
                    >
                        {{ scope.row.action_button_link }}
                    </el-link>
                    <span v-else>—</span>
                </template>
            </el-table-column>

            <!-- Sempre ativo -->
            <el-table-column label="Sempre ativo" width="120" align="center">
                <template slot-scope="scope">
                    <el-tag size="small" :type="scope.row.always_active ? 'success' : 'danger'">
                        {{ scope.row.always_active ? 'SIM' : 'NÃO' }}
                    </el-tag>
                </template>
            </el-table-column>

            <!-- Acoes -->
            <el-table-column
                fixed="right"
                align="right"
                width="50"
            >
                <div slot="header" style="width: 100%;">
                    <i class="el-icon-menu" />
                </div>

                <template slot-scope="scope">
                    <div class="d-flex justify-content-between">
                        <el-popover placement="bottom-end" trigger="click">
                            <el-button
                                slot="reference"
                                size="mini"
                                type="info"
                                plain
                                circle
                                :disabled="loading"
                            >
                                <i class="el-icon-more"></i>
                            </el-button>

                            <div :style="{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '4px', maxWidth: '300px' }">
                                <span class="font-weight-bold">Ações</span>
                                <el-divider class="my-1"></el-divider>

                                <div :style="{ width: '100%'}">
                                    <el-button
                                        size="mini"
                                        type="warning"
                                        plain
                                        @click="dispatchPopupEdit(scope.row.id)"
                                        :style="{ width: '100%'}"
                                    >
                                        <i class="el-icon-edit"></i>
                                        <span>Editar</span>
                                    </el-button>
                                </div>

                                <div :style="{ width: '100%'}">
                                    <el-button
                                        size="mini"
                                        type="danger"
                                        plain
                                        @click="dispatchPopupDelete(scope.row.id)"
                                        :style="{ width: '100%'}"
                                    >
                                        <i class="el-icon-delete"></i>
                                        <span>Excluir</span>
                                    </el-button>
                                </div>
                            </div>
                        </el-popover>
                    </div>
                </template>
            </el-table-column>

            <template slot="empty">
                <el-empty description="Nenhum popup disponível"></el-empty>
            </template>
        </el-table>

        <!-- Paginacao -->
        <el-row class="mt-3">
            <el-col>
                <el-pagination
                    background
                    :current-page.sync="pagination.data.currentPage"
                    :page-sizes="pagination.pageSizes"
                    :page-size="pagination.data.perPage"
                    layout="total, sizes, prev, pager, next"
                    :total="pagination.total"
                    @current-change="handlePageChange"
                    @size-change="handleSizeChange"
                />
            </el-col>
        </el-row>

        <!-- PopupEdit -->
        <PopupEditModal ref="popupEdit" @updated="fetch"/>

        <!-- DeleteModal -->
        <PopupDeleteModal ref="popupDelete" @deleted="fetch"/>
    </div>
</template>

<script>
    // Components
    import {
        Empty,
        Table,
        TableColumn,
        Notification,
        Popover,
        Tag,
        Divider,
        Image,
        Link,
        RadioGroup,
        RadioButton,
    } from "element-ui";

    import PopupEditModal from "./PopupEditModal.vue";
    import PopupDeleteModal from "./PopupDeleteModal.vue";

    // Api
    import { PopupApi } from "@/api/PopupApi";
    const popupApi = new PopupApi();

    export default {
        name: "popup-list",
        data() {
            return {
                data: [],
                loading: false,
                filters: {
                    always_active: null,
                },
                pagination: {
                    data: {
                        currentPage: 1,
                        perPage: 15,
                    },
                    pageSizes: [15, 50, 75, 100],
                    total: 0
                },
            }
        },
        methods: {
            async fetch() {
                try {
                    this.loading = true;

                    const res = await popupApi.list({
                        ...this.filters,
                        ...this.pagination.data
                    });

                    this.data = res.data;
                    this.pagination.total = res.total;
                } catch (err) {
                    Notification.error({
                        title: 'Erro',
                        message: 'Não foi possível obter os popups!'
                    });
                } finally {
                    this.loading = false;
                }
            },
            /**
             * Editar
             */
            async dispatchPopupEdit(id) {
                this.$refs.popupEdit.id = id;

                const elLoading = this.$loading({ lock: true })

                await this.$refs.popupEdit.show();

                elLoading.close();

                return this.$refs.popupEdit.toggleModal();
            },

            /** Excluir */
            async dispatchPopupDelete(id) {
                this.$refs.popupDelete.id = id;

                const elLoading = this.$loading({ lock: true })

                await this.$refs.popupDelete.show();

                elLoading.close();

                return this.$refs.popupDelete.toggleModal();
            },

            /*
             * Pagination
             */
            handlePageChange(page) {
                this.pagination.data.currentPage = page;
                this.fetch();
            },
            handleSizeChange(val) {
                this.pagination.data.perPage = val;
                this.pagination.data.currentPage = 1;
                this.fetch();
            },
        },
        watch: {
            "filters": {
                deep: true,
                handler() {
                    this.pagination.data.currentPage = 1;
                    this.fetch();
                },
            }
        },
        mounted() {
            this.fetch();
        },
        components: {
            [Empty.name]: Empty,
            [Table.name]: Table,
            [TableColumn.name]: TableColumn,
            [Notification.name]: Notification,
            [Popover.name]: Popover,
            [Tag.name]: Tag,
            [Divider.name]: Divider,
            [Image.name]: Image,
            [Link.name]: Link,
            [RadioGroup.name]: RadioGroup,
            [RadioButton.name]: RadioButton,
            PopupEditModal,
            PopupDeleteModal,
        },
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/_macro/popup/PopupList.vue